// Facebook Instagram Integration Setup Component
// React component for setting up Facebook and Instagram integration

import React, { useState } from 'react';
import { FacebookInstagramIntegrationFactory } from '../../services/integrations/social/FacebookInstagramIntegrationFactory';

interface FacebookInstagramSetupProps {
  onComplete: (integrationId: string) => void;
  onCancel: () => void;
  existingConfig?: any;
}

interface SetupStep {
  id: string;
  title: string;
  description: string;
  completed: boolean;
}

interface AvailablePage {
  id: string;
  name: string;
  category: string;
  hasInstagram: boolean;
  instagramUsername?: string;
}

export const FacebookInstagramIntegrationSetup: React.FC<FacebookInstagramSetupProps> = ({
  onComplete,
  onCancel,
  existingConfig
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availablePages, setAvailablePages] = useState<AvailablePage[]>([]);
  
  const [formData, setFormData] = useState({
    name: existingConfig?.name || 'Facebook & Instagram Integration',
    accessToken: existingConfig?.credentials?.accessToken || '',
    appId: existingConfig?.credentials?.appId || '',
    appSecret: existingConfig?.credentials?.appSecret || '',
    selectedPages: existingConfig?.credentials?.pageId ? [existingConfig.credentials.pageId] : [],
    enableFacebook: existingConfig?.settings?.enableFacebook ?? true,
    enableInstagram: existingConfig?.settings?.enableInstagram ?? true,
    audienceSyncEnabled: existingConfig?.settings?.audienceSync?.enabled ?? true,
    engagementTrackingEnabled: existingConfig?.settings?.engagementTracking?.enabled ?? true
  });

  const steps: SetupStep[] = [
    {
      id: 'authentication',
      title: 'Connect to Facebook',
      description: 'Authenticate with Facebook and grant necessary permissions',
      completed: false
    },
    {
      id: 'page_selection',
      title: 'Select Pages',
      description: 'Choose which Facebook Pages and Instagram accounts to connect',
      completed: false
    },
    {
      id: 'platform_settings',
      title: 'Platform Settings',
      description: 'Configure Facebook and Instagram integration settings',
      completed: false
    },
    {
      id: 'sync_settings',
      title: 'Sync Settings',
      description: 'Configure data synchronization preferences',
      completed: false
    }
  ];

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const testCredentials = async () => {
    if (!formData.accessToken || !formData.appId || !formData.appSecret) {
      setError('Please fill in all required fields');
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await FacebookInstagramIntegrationFactory.testCredentials({
        accessToken: formData.accessToken,
        appId: formData.appId,
        appSecret: formData.appSecret
      });

      if (result.success) {
        setAvailablePages(result.availablePages || []);
        return true;
      } else {
        setError(result.message);
        return false;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to test credentials');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (currentStep === 0) {
      // Test credentials before proceeding
      const credentialsValid = await testCredentials();
      if (!credentialsValid) return;
    }

    if (currentStep === 1) {
      // Validate page selection
      if (formData.selectedPages.length === 0) {
        setError('Please select at least one Facebook page');
        return;
      }
    }

    if (currentStep === 2) {
      // Validate platform settings
      if (!formData.enableFacebook && !formData.enableInstagram) {
        setError('Please enable at least one platform');
        return;
      }
    }

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setError(null);
    } else {
      await handleComplete();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setError(null);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    setError(null);

    try {
      const integration = await FacebookInstagramIntegrationFactory.createFromWizard(formData);
      onComplete(integration.getConfig().id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create integration');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-content mb-4">
                Facebook App Credentials
              </h3>
              <p className="text-sm text-content-secondary mb-6">
                Enter your Facebook App credentials to connect your pages and Instagram accounts.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Integration Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-line-strong rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter integration name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Page Access Token *
                </label>
                <input
                  type="password"
                  value={formData.accessToken}
                  onChange={(e) => handleInputChange('accessToken', e.target.value)}
                  className="w-full px-3 py-2 border border-line-strong rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your Facebook Page Access Token"
                />
                <p className="text-xs text-content-muted mt-1">
                  Get this from Facebook Developer Console → Your App → Tools → Graph API Explorer
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  App ID *
                </label>
                <input
                  type="text"
                  value={formData.appId}
                  onChange={(e) => handleInputChange('appId', e.target.value)}
                  className="w-full px-3 py-2 border border-line-strong rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your Facebook App ID"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  App Secret *
                </label>
                <input
                  type="password"
                  value={formData.appSecret}
                  onChange={(e) => handleInputChange('appSecret', e.target.value)}
                  className="w-full px-3 py-2 border border-line-strong rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your Facebook App Secret"
                />
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-md">
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">Required Permissions</h4>
              <ul className="text-sm text-blue-800 dark:text-blue-300 space-y-1">
                <li>• pages_manage_posts - Publish posts to Facebook pages</li>
                <li>• pages_read_engagement - Read engagement metrics</li>
                <li>• instagram_basic - Access Instagram account info</li>
                <li>• instagram_content_publish - Publish to Instagram</li>
                <li>• ads_management - Manage custom audiences</li>
              </ul>
            </div>
          </div>
        );

      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-content mb-4">
                Select Facebook Pages
              </h3>
              <p className="text-sm text-content-secondary mb-6">
                Choose which Facebook Pages you want to manage through this integration.
              </p>
            </div>

            {availablePages.length > 0 ? (
              <div className="space-y-3">
                {availablePages.map((page) => (
                  <div
                    key={page.id}
                    className="flex items-center p-4 border border-line rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700"
                  >
                    <input
                      type="checkbox"
                      id={`page-${page.id}`}
                      checked={formData.selectedPages.includes(page.id)}
                      onChange={(e) => {
                        const newSelection = e.target.checked
                          ? [...formData.selectedPages, page.id]
                          : formData.selectedPages.filter(id => id !== page.id);
                        handleInputChange('selectedPages', newSelection);
                      }}
                      className="h-4 w-4 text-blue-600 dark:text-blue-300 focus:ring-blue-500 border-line-strong rounded"
                    />
                    <div className="ml-3 flex-1">
                      <label htmlFor={`page-${page.id}`} className="block text-sm font-medium text-content">
                        {page.name}
                      </label>
                      <p className="text-sm text-content-muted">
                        Category: {page.category}
                        {page.hasInstagram && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                            Instagram: @{page.instagramUsername}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-content-muted">No pages found. Please check your credentials and permissions.</p>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-content mb-4">
                Platform Settings
              </h3>
              <p className="text-sm text-content-secondary mb-6">
                Configure which platforms you want to enable for this integration.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="enableFacebook"
                  checked={formData.enableFacebook}
                  onChange={(e) => handleInputChange('enableFacebook', e.target.checked)}
                  className="h-4 w-4 text-blue-600 dark:text-blue-300 focus:ring-blue-500 border-line-strong rounded"
                />
                <div className="ml-3">
                  <label htmlFor="enableFacebook" className="text-sm font-medium text-content">
                    Enable Facebook Integration
                  </label>
                  <p className="text-sm text-content-muted">
                    Publish posts and manage Facebook pages
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="enableInstagram"
                  checked={formData.enableInstagram}
                  onChange={(e) => handleInputChange('enableInstagram', e.target.checked)}
                  className="h-4 w-4 text-blue-600 dark:text-blue-300 focus:ring-blue-500 border-line-strong rounded"
                />
                <div className="ml-3">
                  <label htmlFor="enableInstagram" className="text-sm font-medium text-content">
                    Enable Instagram Integration
                  </label>
                  <p className="text-sm text-content-muted">
                    Publish posts to Instagram Business accounts
                  </p>
                </div>
              </div>
            </div>

            {formData.enableInstagram && (
              <div className="bg-yellow-50 dark:bg-yellow-900/30 p-4 rounded-md">
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  <strong>Note:</strong> Instagram integration requires Instagram Business accounts 
                  connected to your selected Facebook pages.
                </p>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-content mb-4">
                Sync Settings
              </h3>
              <p className="text-sm text-content-secondary mb-6">
                Configure how data should be synchronized between platforms.
              </p>
            </div>

            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="audienceSyncEnabled"
                  checked={formData.audienceSyncEnabled}
                  onChange={(e) => handleInputChange('audienceSyncEnabled', e.target.checked)}
                  className="h-4 w-4 text-blue-600 dark:text-blue-300 focus:ring-blue-500 border-line-strong rounded"
                />
                <div className="ml-3">
                  <label htmlFor="audienceSyncEnabled" className="text-sm font-medium text-content">
                    Enable Audience Synchronization
                  </label>
                  <p className="text-sm text-content-muted">
                    Sync email segments with Facebook Custom Audiences for targeted advertising
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="engagementTrackingEnabled"
                  checked={formData.engagementTrackingEnabled}
                  onChange={(e) => handleInputChange('engagementTrackingEnabled', e.target.checked)}
                  className="h-4 w-4 text-blue-600 dark:text-blue-300 focus:ring-blue-500 border-line-strong rounded"
                />
                <div className="ml-3">
                  <label htmlFor="engagementTrackingEnabled" className="text-sm font-medium text-content">
                    Enable Engagement Tracking
                  </label>
                  <p className="text-sm text-content-muted">
                    Track social media engagement and update lead profiles automatically
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-md">
              <h4 className="text-sm font-medium text-green-900 dark:text-green-300 mb-2">Integration Summary</h4>
              <ul className="text-sm text-green-800 dark:text-green-300 space-y-1">
                <li>• Pages selected: {formData.selectedPages.length}</li>
                <li>• Facebook enabled: {formData.enableFacebook ? 'Yes' : 'No'}</li>
                <li>• Instagram enabled: {formData.enableInstagram ? 'Yes' : 'No'}</li>
                <li>• Audience sync: {formData.audienceSyncEnabled ? 'Enabled' : 'Disabled'}</li>
                <li>• Engagement tracking: {formData.engagementTrackingEnabled ? 'Enabled' : 'Disabled'}</li>
              </ul>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div
              key={step.id}
              className={`flex items-center ${index < steps.length - 1 ? 'flex-1' : ''}`}
            >
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  index <= currentStep
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'border-line-strong text-content-muted'
                }`}
              >
                {index < currentStep ? (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <span className="text-sm font-medium">{index + 1}</span>
                )}
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-4 ${
                    index < currentStep ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="mt-4">
          <h2 className="text-xl font-semibold text-content">
            {steps[currentStep].title}
          </h2>
          <p className="text-sm text-content-secondary mt-1">
            {steps[currentStep].description}
          </p>
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-surface rounded-lg border border-line p-6 mb-6">
        {renderStepContent()}
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-md">
          <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <div>
          {currentStep > 0 && (
            <button
              onClick={handleBack}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-surface border border-line-strong rounded-md hover:bg-gray-50 dark:hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              Back
            </button>
          )}
        </div>
        
        <div className="flex space-x-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-surface border border-line-strong rounded-md hover:bg-gray-50 dark:hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Cancel
          </button>
          
          <button
            onClick={handleNext}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? (
              <div className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {currentStep === steps.length - 1 ? 'Creating...' : 'Testing...'}
              </div>
            ) : (
              currentStep === steps.length - 1 ? 'Complete Setup' : 'Next'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};